import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    input,
    linkedSignal,
    signal,
    ViewEncapsulation,
    effect,
} from "@angular/core";
import { TypeManagerDecorator } from "../../../../main/type.map.service";
import { Contact } from "../../../../pojo/Contact";
import { Item } from "../../../../pojo/Item";
import { S25Util } from "../../../../util/s25-util";
import { S25LoadingApi } from "../../../s25-loading/loading.api";
import { ContactService } from "../../../../services/contact.service";
import { Proto } from "../../../../pojo/Proto";
import ServiceStatus = Proto.ServiceStatus;
import { ObjectDetailsUtil } from "../../../../util/object.details.util";
import { S25Const } from "../../../../util/s25-const";
import { DropDownItem } from "../../../../pojo/DropDownItem";
import { Bind } from "../../../../decorators/bind.decorator";
import { MultiselectModelI } from "../../../s25-multiselect/s25.multiselect.component";
import { S25ItemI } from "../../../../pojo/S25ItemI";

type EditableOrgs = Contact.DetailData["editableOrgs"];

@TypeManagerDecorator("s25-ng-editable-contact-orgs")
@Component({
    selector: "s25-ng-editable-contact-orgs",
    template: `
        <s25-loading-inline [model]="{}"></s25-loading-inline>
        @if (editableOrgs().length) {
            @for (org of editableOrgs(); track org.itemId) {
                <div class="org-wrapper c-margin-bottom--quarter" [class.read-only]="!canEdit()">
                    <div class="org-row">
                        <div class="org-header">
                            @if (canEdit()) {
                                <button class="c-textButton" (click)="update(org)" aria-label="Remove organization">
                                    <s25-ng-icon [type]="'close'" [ariaLabel]="false" />
                                </button>
                            }

                            <s25-item
                                [itemId]="org.itemId"
                                [itemTypeId]="Item.Ids.Organization"
                                [hideLabel]="false"
                                [includeTypeIcon]="true"
                            ></s25-item>
                        </div>
                        @let filteredRoles = getFilteredRoles(org);
                        @if (canEdit()) {
                            <div class="org-roles">
                                <s25-ng-multiselect-search-criteria
                                    [type]="'organizationRoles'"
                                    [selectedItems]="getEditorSelectedRoles(org)"
                                    [modelBean]="getModelBean(org)"
                                    (onDone)="updateRole(org, 'mod', $event)"
                                    [showResults]="true"
                                    [popoverOnBody]="true"
                                />
                            </div>
                        } @else {
                            <p class="ngItalic">
                                @for (role of filteredRoles; track role.itemId; let isLast = $last) {
                                    {{ role.itemName }}
                                    @if (!isLast) {
                                        <span>, </span>
                                    }
                                }
                            </p>
                        }
                    </div>
                </div>
            }
        } @else {
            <p class="no-results">No Results</p>
        }
        @if (canEdit()) {
            <div class="add-org-wrapper c-margin-top--half">
                <label class="ngBold">Add Organization</label>
                <s25-ng-dropdown-search-criteria
                    [type]="'organizations'"
                    [(value)]="newOrg"
                    [ariaLabel]="'Add Organization'"
                    [filter]="filterOrgs"
                    (valueChange)="newOrg.set($event); add()"
                />
            </div>
            @if (duplicateOrgMsg()) {
                <p class="ngFinePrint ngRed c-margin-top--half">{{ duplicateOrgMsg() }}</p>
            }
        }
    `,
    styles: `
        @container contactorgs (width <= 600px) {
            .org-wrapper {
                display: grid;
                grid-template-columns: 1fr;
                row-gap: 6px;
            }
            .add-org-wrapper {
                gap: 5px !important;
                flex-wrap: wrap;

                s25-ng-dropdown-search-criteria {
                    flex-basis: 96% !important;
                    margin-bottom: 5px;
                }
            }

            .add-org-wrapper {
                margin-top: 0 !important;
                margin-left: 5px;
                s25-ng-dropdown-search-criteria {
                    flex-basis: 100% !important;
                }
            }

            .org-header {
                font-weight: 600;
                font-size: 15px;
                margin-bottom: 8px;
                display: flex;
                align-items: center;
            }

            .org-roles {
                display: flex;
                flex-wrap: wrap;
                gap: 6px;
            }
        }

        .org-row {
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            padding: 12px 16px;
            margin-bottom: 12px;
        }

        :host ::ng-deep .nm-party--on .org-row {
            background-color: #3c3d46;
            --border-color: rgba(255, 255, 255, 0.24);
            border-color: hsla(0, 0%, 100%, 0.24);
            color: #fff;
        }

        .s25-item-label {
            padding-left: 10px;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class S25EditableContactOrgsComponent {
    itemId = input<number>();
    canEdit = input<boolean>();
    orgs = input<EditableOrgs>();
    initOrgs: EditableOrgs = [];

    editableOrgs = linkedSignal(() => S25Util.array.forceArray(this.orgs()).sort(S25Util.shallowSort("itemName")));

    duplicateOrgMsg = signal("");
    newOrg = signal<(DropDownItem & { status?: string }) | undefined>(undefined);

    private orgModelBeans = new Map<number, MultiselectModelI>();

    protected readonly Item = Item;

    constructor(private el: ElementRef) {
        // Clone initial orgs after first load,
        effect(() => {
            const orgs = this.orgs();
            if (orgs && this.initOrgs.length === 0) this.initOrgs = structuredClone(orgs);
        });
    }

    async save(updatedOrgs: EditableOrgs) {
        if (!updatedOrgs) return;
        S25LoadingApi.init(this.el.nativeElement);
        const payload: Array<{
            status: ServiceStatus;
            organization_id: number;
            organization_name: string;
            role_id: number;
            role_name: string;
        }> = [];

        updatedOrgs.forEach((org) => {
            const { itemId, itemName, roles, status } = org;
            const orgStatus = status ?? "est";
            const orgId = Number(itemId);
            if (orgStatus === "del") {
                // If org status is 'del', delete all roles for this org
                if (roles && roles.length > 0) {
                    roles.forEach((role) => {
                        payload.push({
                            status: "del",
                            organization_id: orgId,
                            organization_name: itemName ?? "",
                            role_id: Number(role.itemId) ?? S25Const.unassignedRole.organization,
                            role_name: role.itemName ?? "",
                        });
                    });
                } else {
                    // If no roles array, create a delete entry with unassigned role, otherwise service will not del org
                    payload.push({
                        status: "del",
                        organization_id: orgId,
                        organization_name: itemName ?? "",
                        role_id: S25Const.unassignedRole.organization,
                        role_name: "",
                    });
                }
            } else {
                // If org status is 'est' (or 'new', 'mod'), keep roles as 'est'
                if (roles && roles.length > 0) {
                    roles.forEach((role) => {
                        payload.push({
                            status: orgStatus === "new" || orgStatus === "mod" ? orgStatus : "est",
                            organization_id: orgId,
                            organization_name: itemName ?? "",
                            role_id: Number(role.itemId) ?? S25Const.unassignedRole.organization,
                            role_name: role.itemName ?? "",
                        });
                    });
                } else {
                    // If no roles array, create entry with unassigned role, otherwise got serviceerror
                    payload.push({
                        status: orgStatus === "new" || orgStatus === "mod" ? orgStatus : "est",
                        organization_id: orgId,
                        organization_name: itemName ?? "",
                        role_id: S25Const.unassignedRole.organization,
                        role_name: "",
                    });
                }
            }
        });

        const [_, error] = await S25Util.Maybe(
            ContactService.putContactMinimal(this.itemId()!, { organization: payload }),
        );
        S25LoadingApi.destroy(this.el.nativeElement);
        if (error) return S25Util.showError(error);
        this.editableOrgs.set(
            updatedOrgs
                .filter((org) => org.status !== "del")
                .map((org) => (["new", "mod"].includes(<string>org.status) ? { ...org, status: "est" } : org)),
        );
    }

    add() {
        this.duplicateOrgMsg.set("");
        if (!this.editableOrgs().some((org) => org.itemId === this.newOrg()?.itemId)) {
            const updatedOrgs = [...this.editableOrgs(), { ...this.newOrg(), status: "new" }];
            this.newOrg.set(undefined);
            return this.save(<EditableOrgs>updatedOrgs);
        }
        this.duplicateOrgMsg.set(
            `This contact is already associated with ${this.newOrg()?.itemName ?? "the selected organization"}`,
        );
    }

    update(org: EditableOrgs[number]) {
        // ACCL-2477 api needs to remove old org role and add new one to replace it instead of modifying the original
        const updatedOrgs = [
            ...this.editableOrgs().map((o) => (o.itemId === org.itemId ? { ...o, status: "del" } : o)),
        ];
        return this.save(<EditableOrgs>updatedOrgs);
    }

    updateRole(org: EditableOrgs[number], status?: ServiceStatus, newRoles?: S25ItemI[]) {
        if (!newRoles) return;
        // Get the initial roles for this org from the cloned initOrgs
        const initOrg = this.initOrgs?.find((o) => o.itemId === org.itemId);
        const initRoles = initOrg?.roles || [];
        // Create sets for easy lookup
        const initRoleIds = new Set(initRoles.map((r) => r.itemId));
        const newRoleIds = new Set(newRoles.map((r) => r.itemId));
        // Build roles with status
        const rolesWithStatus: Array<DropDownItem & { status: ServiceStatus }> = [];
        // Roles in initial but NOT in new → 'del'
        for (const role of initRoles) {
            if (!newRoleIds.has(role.itemId)) {
                rolesWithStatus.push({
                    itemId: role.itemId,
                    itemName: role.itemName,
                    status: "del",
                });
            }
        }
        // Roles in new selection
        for (const role of newRoles) {
            if (initRoleIds.has(role?.itemId)) {
                // Exists in both → 'est' (established)
                rolesWithStatus.push({
                    itemId: role.itemId,
                    itemName: role.itemName,
                    status: role.checked ? "est" : "del",
                });
            } else {
                // Only in new → 'new'
                rolesWithStatus.push({
                    itemId: role.itemId,
                    itemName: role.itemName,
                    status: "new",
                });
            }
        }
        // Update the org with the new roles (keeping only non-deleted for display)
        const displayRoles = rolesWithStatus.filter((r) => r.status !== "del");
        const updatedOrgs = this.editableOrgs().map((o) =>
            o.itemId === org.itemId
                ? {
                      ...o,
                      roles: displayRoles,
                      _rolesWithStatus: rolesWithStatus, // Keep full list with statuses for save
                  }
                : o,
        );

        this.editableOrgs.set(updatedOrgs as EditableOrgs);
        // Save with the roles that have status
        return this.saveWithRoleStatus(org, rolesWithStatus);
    }

    async saveWithRoleStatus(
        org: EditableOrgs[number],
        rolesWithStatus: Array<DropDownItem & { status: ServiceStatus }>,
    ) {
        S25LoadingApi.init(this.el.nativeElement);
        const noRoles = rolesWithStatus.filter((r) => Number(r.itemId) !== S25Const.unassignedRole.organization);
        const hasMultipleRoles = noRoles.length > 1;
        // Build payload entries for this org's roles
        const payload = rolesWithStatus.map((role) => {
            const roleId = Number(role.itemId);
            let status = role.status;
            // If organization has more than one role and this is -2 /no role, change status to 'del'
            if (hasMultipleRoles && roleId === S25Const.unassignedRole.organization) status = "del";
            return {
                status: status,
                organization_id: Number(org.itemId),
                organization_name: org.itemName ?? "",
                role_id: roleId ?? S25Const.unassignedRole.organization,
                role_name: role.itemName ?? "",
            };
        });

        const [_, error] = await S25Util.Maybe(
            ContactService.putContactMinimal(this.itemId()!, { organization: payload }),
        );
        S25LoadingApi.destroy(this.el.nativeElement);
        if (error) return S25Util.showError(error);
    }

    @Bind
    getModelBean(org: EditableOrgs[number]): MultiselectModelI {
        const orgId = Number(org.itemId);
        if (!this.orgModelBeans.has(orgId)) {
            this.orgModelBeans.set(orgId, {
                removeAction: (removedItem: S25ItemI) => this.onItemRemoved(org, removedItem),
            });
        }
        return this.orgModelBeans.get(orgId)!;
    }

    onItemRemoved(org: EditableOrgs[number], removedItem: S25ItemI) {
        this.updateRole(org, "mod", org.roles);
    }

    @Bind
    filterOrgs(org: DropDownItem) {
        return !this.editableOrgs().some((o) => o.itemId === org.itemId);
    }

    getFilteredRoles(org: EditableOrgs[number]): S25ItemI[] {
        return ObjectDetailsUtil.organizationRolesForReadOnlyDisplay(org.roles);
    }

    getEditorSelectedRoles(org: EditableOrgs[number]): S25ItemI[] {
        return ObjectDetailsUtil.organizationRolesForMultiselectSelection(org.roles);
    }
}
